import { useMediaQuery } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalViews } from '../../../constants';
import { StockCountItem, StockInventoryCountUpdatedItems, StockStatusType } from '../../../hooks';
import { Icons, Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations, Button, Checkbox, Modal } from '../../../stories/molecules';
import { muiTheme } from '../../../theme';
import { commonTranslations, yearlyStockTakeTranslations } from '../../../translations';
import { StateView } from '../stateView/StateView';
import { StockDetailsCard } from '../stockDetailsCard/StockDetailsCard';

type InventoryCheckModalProps = {
  isOpen: boolean;
  stockModalDetails: StockCountItem[];
  modalView: ModalViews;
  isLoading: boolean;
  onClose: () => void;
  updateStock: (updatedItems: StockInventoryCountUpdatedItems[]) => void;
};

export const InventoryCheckModal: FC<InventoryCheckModalProps> = ({
  isOpen,
  stockModalDetails,
  modalView,
  isLoading,
  onClose,
  updateStock,
}) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('tabletLandscape'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const {
    singleAction: {
      foundItem: foundItemTranslations,
      notFoundItem: notFoundTranslations,
      checkboxes: checkboxTranslations,
    },
  } = yearlyStockTakeTranslations;

  const [checkedBackstore, setCheckedBackstore] = useState(false);
  const [checkedFrontstore, setCheckedFrontstore] = useState(false);
  const [checkedPick, setCheckedPick] = useState(false);

  const stockItem = stockModalDetails[0];

  useEffect(() => {
    if (!isOpen) {
      setCheckedBackstore(false);
      setCheckedFrontstore(false);
      setCheckedPick(false);
    }
  }, [isOpen]);

  const handleConfirm = (item: StockCountItem) => {
    let updatedItems: StockInventoryCountUpdatedItems[] = [];
    if (item.status === 'Pick') {
      if (checkedBackstore && checkedFrontstore) {
        updatedItems = [{ sicScope: item.sicScope, status: 'NotFound' }];
      }
    } else {
      updatedItems = [{ sicScope: item.sicScope, status: checkedPick ? 'Picked' : 'Pick' }];
    }
    updateStock(updatedItems);
  };

  const getStockStatus = (item: StockCountItem): { text: string; status: StockStatusType } => {
    return item.status === 'Pick'
      ? {
          text: t(notFoundTranslations.successText.key, notFoundTranslations.successText.defaultValue),
          status: 'NotFound',
        }
      : checkedPick
      ? {
          text: t(foundItemTranslations.successText2.key, foundItemTranslations.successText2.defaultValue),
          status: 'Picked',
        }
      : {
          text: t(foundItemTranslations.successText1.key, foundItemTranslations.successText1.defaultValue),
          status: 'Pick',
        };
  };

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <ColumnFlexContainer
        data-testid="inventory-check-modal-wrapper"
        minWidth={isDesktop ? [150] : ''}
        padding={isMobile ? [4] : isTablet ? [8, 10] : [8]}
      >
        {stockItem && modalView === ModalViews.INITIAL && (
          <>
            <ColumnFlexContainer padding={[0, 0, 4]}>
              <Animation
                title=""
                size="x-small"
                animation={stockItem.status === 'Pick' ? Animations.CONFUSED : Animations.HAPPY}
              />
              <Typography type="h4" textAlign="center" margin={isMobile ? [0, 0, 2] : [0]} overflow="visible">
                {stockItem.status === 'Pick'
                  ? t(notFoundTranslations.modalTitle.key, notFoundTranslations.modalTitle.defaultValue)
                  : t(foundItemTranslations.modalTitle.key, foundItemTranslations.modalTitle.defaultValue)}
              </Typography>
              <Typography textAlign="center" overflow="visible">
                {stockItem.status === 'Pick'
                  ? t(notFoundTranslations.modalSubtitle.key, notFoundTranslations.modalSubtitle.defaultValue)
                  : t(foundItemTranslations.modalSubtitle.key, foundItemTranslations.modalSubtitle.defaultValue)}
              </Typography>
            </ColumnFlexContainer>
            <StockDetailsCard
              stockCountItem={stockItem}
              status={stockItem.status}
              isBulkAction={false}
              footerContent={
                stockItem.status === 'Pick' ? (
                  <>
                    <Checkbox
                      onChange={(checked: boolean) => setCheckedBackstore(checked)}
                      isChecked={checkedBackstore}
                      label={t(
                        checkboxTranslations.checkBackstore.key,
                        checkboxTranslations.checkBackstore.defaultValue,
                      )}
                    />
                    <Checkbox
                      onChange={(checked: boolean) => setCheckedFrontstore(checked)}
                      isChecked={checkedFrontstore}
                      label={t(
                        checkboxTranslations.checkFrontstore.key,
                        checkboxTranslations.checkFrontstore.defaultValue,
                      )}
                    />
                  </>
                ) : (
                  <Checkbox
                    onChange={(checked: boolean) => setCheckedPick(checked)}
                    isChecked={checkedPick}
                    label={t(checkboxTranslations.pickItems.key, checkboxTranslations.pickItems.defaultValue)}
                  />
                )
              }
            />
            <RowFlexContainer flex={1} gap={[2]} padding={[3, 0, 0]}>
              <Button fullWidth variant="secondary" label="Cancel" onClick={onClose} />
              <Button
                fullWidth
                data-testid={stockItem.status === 'Pick' ? 'confirm-button-notFound' : 'confirm-button-found'}
                disabled={stockItem.status === 'Pick' && (!checkedBackstore || !checkedFrontstore)}
                isLoading={isLoading}
                label={
                  stockItem.status === 'Pick'
                    ? t(notFoundTranslations.modalButton.key, notFoundTranslations.modalButton.defaultValue)
                    : t(foundItemTranslations.modalButton.key, foundItemTranslations.modalButton.defaultValue)
                }
                onClick={() => handleConfirm(stockItem)}
              />
            </RowFlexContainer>
          </>
        )}
        {stockItem && modalView === ModalViews.SUCCESS && (
          <StateView
            stockItems={
              <StockDetailsCard
                isBulkAction={false}
                status={getStockStatus(stockItem).status}
                stockCountItem={stockItem}
              />
            }
            color="validationGreen"
            title={t(commonTranslations.successText.key, commonTranslations.successText.defaultValue)}
            icon={Icons.CHECK_MARK_CIRCLE}
            subTitle={getStockStatus(stockItem).text}
            onClose={onClose}
          />
        )}

        {stockItem && modalView === ModalViews.ERROR && (
          <StateView
            stockItems={
              <StockDetailsCard isBulkAction={false} status={stockItem.status} stockCountItem={stockItem} />
            }
            color="red"
            title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
            icon={Icons.REFRESH}
            subTitle={
              stockItem.status === 'Pick'
                ? t(notFoundTranslations.errorText.key, notFoundTranslations.errorText.defaultValue)
                : checkedPick
                ? t(foundItemTranslations.errorText2.key, foundItemTranslations.errorText2.defaultValue)
                : t(foundItemTranslations.errorText1.key, foundItemTranslations.errorText1.defaultValue)
            }
            onClose={onClose}
          />
        )}
      </ColumnFlexContainer>
    </Modal>
  );
};
